import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/pt-br';
import './styles.css';

import { Box, useToast } from '@chakra-ui/react';
import axios from 'axios';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { lastDayOfWeek, subDays } from 'date-fns';

import ScheduleBookingsModal from './components/ScheduleBookingsModal';
import { useActivity } from '../../../../../../../../../../../hooks/activity';
import { translateError } from '../../../../../../../../../../../utils/errors';
import { EventComponent } from './components/EventComponent';
import { ToolbarComponent } from './components/ToolbarComponent';
import { BookingRegisterModal } from '../BookingRegisterModal';
import {
  IWeekdayActivitySchedule,
  listWeekdaysActivitiesSchedulesService,
} from '../../../../../../../../../../../services/Activities/ListWeekdaysActivitySchedulesService';
import { useCalendarEvents } from '../../../../../../../../../../../hooks/calendarEvent';
import { getSchedulesAvailableVacancies } from '../../../../../../../../../../../utils/activity-schedule-capacity';

const localizer = momentLocalizer(moment);

const getEventAvailabilityState = (
  availableVacancies: number | null,
): { isFull: boolean; title: string } => {
  if (typeof availableVacancies !== 'number') {
    return { isFull: false, title: 'Ilimitado' };
  }

  if (availableVacancies > 0) {
    return { isFull: false, title: `${availableVacancies} vaga(s)` };
  }

  return { isFull: true, title: 'Lotado' };
};

interface ICreateBookingModal {
  action?: 'create-booking' | 'create-booking-queue';
  schedule?: IWeekdayActivitySchedule;
}

export interface ICalendarEvent {
  id: string;
  activityId: string;
  title: string;
  start: Date;
  end: Date;
  schedules: IWeekdayActivitySchedule[];
  isFull?: boolean;
  isActive: boolean;
}

interface ICreateBookingData {
  action?: 'create-booking' | 'create-booking-queue';
}

export const ActivityCalendar: React.FC = () => {
  const toast = useToast();

  const { activity } = useActivity();
  const { calendarEvents, handleCalendarEvents } = useCalendarEvents();

  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(
    subDays(lastDayOfWeek(new Date()), 6),
  );
  const [isScheduleBookingsModalVisible, setIsScheduleBookingsModalVisible] =
    useState<boolean>(false);
  const [selectedCalendarEvent, setSelectedCalendarEvent] =
    useState<ICalendarEvent>();
  const [createBookingData, setCreateBookingData] =
    useState<ICreateBookingData>();
  const [activitySchedule, setActivitySchedule] =
    useState<IWeekdayActivitySchedule>();

  const hasBookingPrice = useMemo(() => {
    if (!activitySchedule) {
      return false;
    }

    return (
      !!activitySchedule?.bookingPrice ||
      !!activitySchedule?.guestAdditionalBookingPrice
    );
  }, [activitySchedule]);

  const loadSchedules = useCallback(
    async (activityId: string) => {
      try {
        setLoading(true);

        const events = await listWeekdaysActivitiesSchedulesService({
          activityId,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          addDay: 6,
        });

        const eventsParsed = events.reduce((parsedEvents, event) => {
          const eventId = event.date + event.startTime + event.endTime;

          const eventIndex = parsedEvents.findIndex(
            (schedule) => schedule.id === eventId,
          );

          if (eventIndex >= 0) {
            const eventsAux = parsedEvents;

            const eventSchedules = [
              ...parsedEvents[eventIndex].schedules,
              event,
            ];

            const { isFull, title } = getEventAvailabilityState(
              getSchedulesAvailableVacancies(eventSchedules),
            );

            eventsAux[eventIndex] = {
              ...parsedEvents[eventIndex],
              schedules: eventSchedules,
              title,
              isFull,
              isActive: event.isActive,
            };

            return eventsAux;
          }

          const { isFull, title } = getEventAvailabilityState(
            getSchedulesAvailableVacancies([event]),
          );

          return [
            ...parsedEvents,
            {
              id: eventId,
              activityId,
              end: new Date(`${event.date} ${event.endTime}`),
              start: new Date(`${event.date} ${event.startTime}`),
              schedules: [event],
              title,
              isFull,
              isActive: event.isActive,
            },
          ];
        }, [] as ICalendarEvent[]);

        handleCalendarEvents(eventsParsed);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os eventos, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [handleCalendarEvents, startDate, toast],
  );

  useEffect(() => {
    if (activity) {
      loadSchedules(activity.id);
    }
  }, [activity, loadSchedules]);

  const toggleScheduleBookingsModalVisible = (): void => {
    setIsScheduleBookingsModalVisible((prevState) => !prevState);
  };
  const handleScheduleBookingsModal = (event?: ICalendarEvent): void => {
    setSelectedCalendarEvent(event);
    setCreateBookingData(undefined);
    toggleScheduleBookingsModalVisible();
  };

  const handleCreateBookingModal = ({
    action,
    schedule,
  }: ICreateBookingModal): void => {
    setActivitySchedule(schedule);
    setCreateBookingData({ action });
    toggleScheduleBookingsModalVisible();
  };

  const handleCloseBookingRegisterModal = (): void => {
    setActivitySchedule(undefined);
    setCreateBookingData(undefined);
  };

  return (
    <Box>
      {!!selectedCalendarEvent && (
        <ScheduleBookingsModal
          eventId={selectedCalendarEvent.id}
          isOpen={isScheduleBookingsModalVisible}
          onClose={handleScheduleBookingsModal}
          onCreateBooking={(schedule) =>
            handleCreateBookingModal({ action: 'create-booking', schedule })
          }
          onCreateBookingQueue={(schedule) =>
            handleCreateBookingModal({
              action: 'create-booking-queue',
              schedule,
            })
          }
        />
      )}

      {!!activitySchedule && (
        <BookingRegisterModal
          action={createBookingData?.action}
          activityScheduleId={activitySchedule.activityScheduleId}
          defaultBookedDate={selectedCalendarEvent?.start}
          hasBookingPrice={hasBookingPrice}
          isOpen={!!activitySchedule}
          startDate={activitySchedule.startDate}
          onClose={handleCloseBookingRegisterModal}
        />
      )}

      <Calendar
        localizer={localizer}
        components={{
          toolbar: (e) => ToolbarComponent({ ...e, loading }),
          event: (e) => EventComponent(e),
        }}
        views={['week', 'day']}
        defaultView="week"
        dayLayoutAlgorithm="no-overlap"
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        style={{ flex: 1 }}
        messages={{
          next: 'Próximo',
          previous: 'Anterior',
          today: 'Hoje',
          month: 'Mês',
          week: 'Semana',
          day: 'Dia',
          agenda: 'Agenda',
          date: 'Data',
          time: 'Hora',
          event: 'Evento',
          allDay: 'Dia todo',
          tomorrow: 'Amanhã',
          yesterday: 'Ontem',
          work_week: 'Dias úteis',
          showMore: (total) => `+${total}`,
        }}
        onSelectEvent={(event) => handleScheduleBookingsModal(event)}
        onRangeChange={(range) => {
          const rangeDate = range as Date[];
          setStartDate(rangeDate[0]);
        }}
        culture="pt-BR"
      />
    </Box>
  );
};
